import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { latest2026Books } from '@/mocks/home';
import { clamp, TRANSITION, COLORS } from './progress';

const categoryDisplayMap: Record<string, string> = {
  科普書: '科普',
  茶書: '茶文化',
  商業書: '商業',
};

function getCategoryDisplay(rawCategory: string): string {
  return categoryDisplayMap[rawCategory] || rawCategory;
}

interface BooksSceneProps {
  progress: number;
}

export default function BooksScene({ progress }: BooksSceneProps) {
  const p = clamp(progress, 0, 1);
  const enter = clamp(p * 3, 0, 1);
  const exit = 1 - clamp((p - 0.82) * 5.5, 0, 1);
  const opacity = Math.min(enter, exit);
  const translateY = (1 - enter) * 60;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const dragRef = useRef({ active: false, startX: 0, startScroll: 0 });

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scrollByAmount = useCallback((dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: 'smooth' });
  }, []);

  // 在書列上滾動滾輪時改為橫向捲動；到達盡頭就放行，讓場景可以繼續推進
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      const goingRight = e.deltaY > 0 || e.deltaX > 0;
      const canScroll = goingRight ? !atEnd : !atStart;
      if (canScroll) {
        el.scrollLeft += e.deltaY || e.deltaX;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    dragRef.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el || !dragRef.current.active) return;
    const delta = e.clientX - dragRef.current.startX;
    el.scrollLeft = dragRef.current.startScroll - delta;
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    dragRef.current.active = false;
    if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 md:px-6 pt-20 md:pt-24 pb-10 pointer-events-none"
      style={{ opacity, transform: `translateY(${translateY}px)`, transition: TRANSITION, willChange: 'transform, opacity' }}
    >
      {/* 標題 */}
      <div className="text-center mb-5 md:mb-7 max-w-2xl">
        <div className="flex items-center justify-center gap-3 mb-3">
          <span className="w-10 h-px bg-white/40" />
          <span className="font-manrope text-white/70 text-[10px] uppercase tracking-[0.35em]">
            Vol. 03 — 最新發行
          </span>
          <span className="w-10 h-px bg-white/40" />
        </div>
        <h2 className="font-semibold text-white text-xl md:text-3xl mb-2">
          2026 最新發行
        </h2>
        <p className="font-manrope text-xs md:text-sm text-white/60 leading-relaxed">
          探索策點文化於 2026 年最新發行的跨領域精選書籍，左右滑動瀏覽更多。
        </p>
      </div>

      {/* 單列橫向書列 */}
      <div className="relative w-full max-w-6xl pointer-events-auto">
        <button
          type="button"
          aria-label="向左滑動書列"
          onClick={() => scrollByAmount(-1)}
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full cursor-pointer transition-opacity duration-200 ${
            canLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ backgroundColor: 'rgba(7,18,48,0.82)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff' }}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="向右滑動書列"
          onClick={() => scrollByAmount(1)}
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full cursor-pointer transition-opacity duration-200 ${
            canRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ backgroundColor: 'rgba(7,18,48,0.82)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff' }}
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div
          ref={scrollerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="no-scrollbar flex gap-3 md:gap-4 overflow-x-auto overflow-y-hidden px-1 md:px-2 py-2 scroll-smooth cursor-grab active:cursor-grabbing select-none"
        >
          {latest2026Books.map((book) => (
            <div
              key={book.id}
              className="group flex flex-col flex-shrink-0 w-40 md:w-44 border"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderColor: COLORS.line }}
            >
              <div
                className="relative w-full overflow-hidden"
                style={{ aspectRatio: '3/4', backgroundColor: 'rgba(255,255,255,0.03)' }}
              >
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  title={`${book.title} ${book.subtitle}`}
                  draggable={false}
                  className="w-full h-full object-contain p-2"
                />
              </div>
              <div className="flex flex-col flex-1 p-2.5 md:p-3">
                <span
                  className="inline-block self-start px-1.5 py-0.5 text-[9px] font-manrope mb-1.5 rounded-sm"
                  style={{ backgroundColor: 'rgba(224,177,94,0.16)', color: COLORS.amber }}
                >
                  {getCategoryDisplay(book.category)}
                </span>
                <h3 className="font-playfair text-white text-xs md:text-[13px] leading-snug mb-1" style={{ fontWeight: 500 }}>
                  {book.title}
                </h3>
                <p className="font-manrope text-[10px] text-white/45 leading-snug">
                  {book.author}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 資料庫入口 */}
      <div className="flex flex-col items-center text-center mt-5 md:mt-6 pointer-events-auto">
        <p className="font-manrope text-[11px] text-white/50 leading-relaxed max-w-lg mb-3">
          完整書名、作者、內容主題及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
        </p>
        <a
          href="https://catalog.sonbook.net/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-300"
          style={{ backgroundColor: COLORS.cream, color: COLORS.ink }}
        >
          前往書籍資料庫
          <i className="ri-arrow-right-up-line text-sm"></i>
        </a>
      </div>
    </div>
  );
}